/**
 * useGuideAvailability Hook
 *
 * Fetches and merges two independent availability data sources:
 *   1. Individual date statuses (guide_availability table via AvailabilityController)
 *   2. Expanded recurring block patterns (guide_availability_recurring_blocks via RecurringBlockController)
 *
 * Merge logic:
 *   - A date blocked by EITHER source is unavailable
 *   - Manual date override (explicit available: true) takes precedence over recurring block
 *   - Source is tracked for display (color coding: green=available, red=manually blocked, blue=recurring)
 *
 * SCRUM-821 AC-17, AC-18, AC-25
 *
 * @param {string} guideId - Guide UUID
 * @param {string} currentMonth - ISO month string (YYYY-MM)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import apiRequest from '../../../services/api';

/**
 * Format a Date object to YYYY-MM-DD string
 */
function toDateString(date) {
  return date.toISOString().split('T')[0];
}

/**
 * Get first and last day of a month
 */
function getMonthRange(monthStr) {
  const [year, month] = monthStr.split('-').map(Number);
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0); // last day of month
  return {
    start: toDateString(startDate),
    end: toDateString(endDate)
  };
}

/**
 * Derive group type from blocked_reason for color treatment (SCRUM-1152)
 */
function deriveGroupType(blockedReason) {
  if (!blockedReason) {return null;}
  if (['trip_day', 'prep_day', 'cleanup_day'].includes(blockedReason)) {return 'booking';}
  if (blockedReason === 'manual') {return 'manual';}
  return null;
}

export default function useGuideAvailability({ guideId, currentMonth }) {
  const [calendarData, setCalendarData] = useState({});
  const [recurringBlocks, setRecurringBlocks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track current fetch to avoid stale updates
  const fetchIdRef = useRef(0);

  // Ref for latest calendarData (used by toggleDate for rollback without dependency)
  const calendarDataRef = useRef({});

  /**
   * Fetch both data sources and merge
   */
  const fetchData = useCallback(async () => {
    if (!guideId || !currentMonth) {
      return;
    }

    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    const { start, end } = getMonthRange(currentMonth);

    try {
      // Fetch all three sources in parallel
      const [calendarRes, expandedRes, statsRes] = await Promise.all([
        apiRequest(`/api/v1/availability/${guideId}/calendar?startDate=${start}&endDate=${end}`),
        apiRequest(`/api/v1/availability/recurring-blocks/guide/${guideId}/expanded?start_date=${start}&end_date=${end}`),
        apiRequest(`/api/v1/availability/recurring-blocks/guide/${guideId}/stats`)
      ]);

      // Stale check
      if (fetchId !== fetchIdRef.current) {
        return;
      }

      // Build merged date map
      const merged = {};

      // 1. Process calendar (individual dates) first
      if (calendarRes.success && calendarRes.data) {
        const calendar = calendarRes.data.calendar || calendarRes.data;
        const processEntry = (dateStr, entry) => {
          const isAvailable = entry.available !== false;
          merged[dateStr] = {
            date: dateStr,
            available: isAvailable,
            source: !isAvailable ? 'manual' : 'default',
            manualOverride: entry.has_manual_entry === true,
            blockedReason: entry.blocked_reason || null,
            blockGroupId: entry.block_group_id || null,
            blockGroupStart: entry.block_group_start || null,
            blockGroupEnd: entry.block_group_end || null,
            blockGroupLabel: entry.block_group_label || null,
            groupType: !isAvailable ? deriveGroupType(entry.blocked_reason) : null,
            patternName: null,
            recurringBlockId: null,
            rrule: null
          };
        };
        if (Array.isArray(calendar)) {
          for (const entry of calendar) {
            processEntry(entry.date, entry);
          }
        } else if (typeof calendar === 'object') {
          for (const [dateStr, entry] of Object.entries(calendar)) {
            processEntry(dateStr, entry);
          }
        }
      }

      // 2. Process expanded recurring blocks
      let recurringBlocksList = [];
      if (expandedRes.success && expandedRes.data) {
        const { blocks = [], blocked_dates: blockedDates = [] } = expandedRes.data;
        recurringBlocksList = blocks;

        // Build maps of date → pattern name and date → recurring block id
        const dateToPattern = {};
        const dateToRecurringBlockId = {};
        const dateToRrule = {};
        for (const block of blocks) {
          if (block.expanded_dates) {
            for (const d of block.expanded_dates) {
              dateToPattern[d] = block.block_name || 'Repeating block';
              dateToRecurringBlockId[d] = block.id || null;
              dateToRrule[d] = block.recurrence_rule || null;
            }
          }
        }

        // Merge recurring blocks into the date map
        for (const dateStr of blockedDates) {
          const existing = merged[dateStr];
          if (existing) {
            // AC-25: Manual override takes precedence
            // If the guide explicitly set available: true, keep it available
            if (existing.manualOverride && existing.available) {
              // Manual available override — do NOT mark as blocked by recurring
              continue;
            }
            // If already manually blocked, keep manual source
            if (existing.source === 'manual') {
              continue;
            }
          }

          // Mark as blocked by repeating block
          merged[dateStr] = {
            date: dateStr,
            available: false,
            source: 'recurring',
            manualOverride: existing?.manualOverride || false,
            blockedReason: null,
            blockGroupId: null,
            blockGroupStart: null,
            blockGroupEnd: null,
            blockGroupLabel: null,
            groupType: 'recurring',
            patternName: dateToPattern[dateStr] || 'Repeating block',
            recurringBlockId: dateToRecurringBlockId[dateStr] || null,
            rrule: dateToRrule[dateStr] || null
          };
        }
      }

      // 3. Fill in remaining dates as available by default
      const startDate = new Date(start);
      const endDate = new Date(end);
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = toDateString(d);
        if (!merged[dateStr]) {
          merged[dateStr] = {
            date: dateStr,
            available: true,
            source: 'default',
            manualOverride: false,
            blockedReason: null,
            blockGroupId: null,
            blockGroupStart: null,
            blockGroupEnd: null,
            blockGroupLabel: null,
            groupType: null,
            patternName: null,
            recurringBlockId: null,
            rrule: null
          };
        }
      }

      setCalendarData(merged);
      setRecurringBlocks(recurringBlocksList);

      // 4. Stats
      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (err) {
      if (fetchId === fetchIdRef.current) {
        setError(err.message || 'Failed to load availability data');
      }
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [guideId, currentMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keep ref in sync with latest calendarData
  useEffect(() => {
    calendarDataRef.current = calendarData;
  }, [calendarData]);

  /**
   * Toggle a single date's availability (AC-08: optimistic update with rollback)
   */
  const toggleDate = useCallback(async (dateStr, newAvailable) => {
    if (!guideId) {
      return;
    }

    // Capture current state for rollback
    const rollbackEntry = calendarDataRef.current[dateStr];

    // Optimistic update — UI changes immediately
    setCalendarData(prev => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        available: newAvailable,
        source: newAvailable ? 'default' : 'manual',
        manualOverride: true
      }
    }));

    try {
      const res = await apiRequest(`/api/v1/availability/${guideId}`, {
        method: 'PUT',
        body: JSON.stringify({ date: dateStr, available: newAvailable }),
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.success) {
        // Revert on non-success response
        setCalendarData(prev => ({
          ...prev,
          [dateStr]: rollbackEntry || prev[dateStr]
        }));
      }

      return res;
    } catch (err) {
      // Revert on error
      setCalendarData(prev => ({
        ...prev,
        [dateStr]: rollbackEntry || prev[dateStr]
      }));
      setError(err.message || 'Failed to update availability');
      throw err;
    }
  }, [guideId]);

  /**
   * Toggle a recurring block's active status (AC-13)
   */
  const toggleBlock = useCallback(async (blockId, isActive) => {
    if (!guideId) {
      return;
    }

    try {
      const res = await apiRequest(`/api/v1/availability/recurring-blocks/${blockId}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: isActive }),
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.success) {
        await fetchData();
      }

      return res;
    } catch (err) {
      setError(err.message || 'Failed to toggle pattern');
      throw err;
    }
  }, [guideId, fetchData]);

  /**
   * Create a new recurring block (AC-14–AC-16)
   */
  const createBlock = useCallback(async (blockData) => {
    if (!guideId) {
      return;
    }

    try {
      const res = await apiRequest('/api/v1/availability/recurring-blocks', {
        method: 'POST',
        body: JSON.stringify({
          guide_id: guideId,
          ...blockData
        }),
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.success) {
        await fetchData();
      }

      return res;
    } catch (err) {
      setError(err.message || 'Failed to create pattern');
      throw err;
    }
  }, [guideId, fetchData]);

  /**
   * Delete a recurring block
   */
  const deleteBlock = useCallback(async (blockId) => {
    if (!guideId) {
      return;
    }

    try {
      const res = await apiRequest(`/api/v1/availability/recurring-blocks/${blockId}`, {
        method: 'DELETE'
      });

      if (res.success) {
        await fetchData();
      }

      return res;
    } catch (err) {
      setError(err.message || 'Failed to delete pattern');
      throw err;
    }
  }, [guideId, fetchData]);

  /**
   * Get info for a specific date
   */
  const getDateInfo = useCallback((dateStr) => {
    return calendarData[dateStr] || {
      date: dateStr,
      available: true,
      source: 'default',
      manualOverride: false,
      blockedReason: null,
      blockGroupId: null,
      blockGroupStart: null,
      blockGroupEnd: null,
      blockGroupLabel: null,
      groupType: null,
      patternName: null,
      recurringBlockId: null,
      rrule: null
    };
  }, [calendarData]);

  return {
    calendarData,
    recurringBlocks,
    stats,
    loading,
    error,
    refresh: fetchData,
    toggleDate,
    toggleBlock,
    createBlock,
    deleteBlock,
    getDateInfo
  };
}
